// ownerIntakeClient — two surfaces for the owner-INTAKE → EPMA front door.
//
//   • PUBLIC, token-gated (no Bearer): the owner-facing intake form at
//     /owner-intake/:token. The token in the path is the only credential, so
//     these calls go straight to fetch (NOT the authed `api` helper) exactly like
//     lib/intakeClient.ts / lib/moveoutFundsClient.ts.
//       - GET  /api/owner-intake/:token              → OwnerIntakeFormView
//       - POST /api/owner-intake/:token              → OwnerIntakeSubmitResult
//       - GET  /api/owner-intake/:token/hoa-choices  → { hoas: KnownHoaChoice[] }
//
//   • AUTHENTICATED staff (Bearer via `api`): kick the intake off.
//       - POST /api/owner-intake/initiate          → InitiateOwnerIntakeResult
//
// LOW-FRICTION HOA STEP: the intake NO LONGER prompts for CC&Rs inline (it jams the
// process). When in an HOA the owner PICKS which HOA (a dropdown of known HOAs +
// "New / not listed") — a server-driven `hoaChoice` select question. The known-HOA
// list is a READ-ONLY selection list (GET /hoa-choices). Choosing "New / not listed"
// reveals a freeform name input. No document is captured in the intake; the CC&Rs are
// collected later via a SEPARATE emailed /ccr-upload/:token link (lib/ccrUploadClient.ts).
//
// Wire shapes are 1:1 with the backend contract in
// apps/api/src/routes/ownerIntake.ts (OwnerIntakeFormView) +
// apps/api/src/lib/ownerIntake.ts (OwnerIntakeQuestion / OwnerIntakeAnswers) +
// apps/api/src/types/db.ts (OwnerIntakeStaffPrefill).

import { api, ApiError } from './api';
import type { OwnerChainPlan } from './ownerSync';
import type { Jurisdiction, OwnerIntakeJurisdiction } from './jurisdiction';

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// ---------------------------------------------------------------------------
// Shared wire types (mirror apps/api)
// ---------------------------------------------------------------------------

/**
 * Per-field staff control on the unified builder:
 *   • yes/no + confirmWithOwner=false → staff answer is final; owner not asked.
 *   • ask_owner                       → owner answers fresh.
 *   • yes/no + confirmWithOwner=true  → owner confirms/corrects the staff answer.
 */
export type OwnerIntakeFieldValue = 'yes' | 'no' | 'ask_owner';

export interface OwnerIntakeFieldControl {
  value: OwnerIntakeFieldValue;
  confirmWithOwner: boolean;
}

/** §23 utilities — each column Included-in-rent (owner pays) or Not (resident pays). */
export type OwnerIntakeUtilityChoice = 'included' | 'not_included';

export type OwnerIntakeUtilityKey =
  | 'water'
  | 'sewer'
  | 'garbage'
  | 'electricity'
  | 'gas'
  | 'yardCare';

export type OwnerIntakePropertyType =
  | 'Single Family Residence'
  | 'Multi Family Residence'
  | 'Commercial';

/**
 * The RAW conditional answers an owner accumulates. Every field optional — only
 * the ones the server marks APPLICABLE are ever asked/set. Keys === question ids.
 */
export interface OwnerIntakeAnswers {
  // §21 "does WHP pay it" — yes = WHP pays, no = owner handles.
  whpPaysPropertyTaxes?: boolean;
  whpPaysInsurance?: boolean;
  whpPaysMortgage?: boolean;
  // Irrigation / winterization.
  hasIrrigation?: boolean;
  needsWinterizing?: boolean;
  winterizationInstructions?: string;
  // HOA.
  inHoa?: boolean;
  wantsWhpPayHoaDues?: boolean;
  /**
   * WHICH HOA — a KNOWN HOA's hoaId, or the sentinel HOA_CHOICE_NEW ("New / not
   * listed"). Intake-only selection data (no inline document upload); the server
   * resolves it into the property↔HOA association at COMPLETE time.
   */
  hoaChoice?: string;
  /** Freeform HOA name — set only when hoaChoice === HOA_CHOICE_NEW. */
  hoaNewName?: string;
  // NO owner-side §24 "other items" freeform — owners answer only structured
  // questions (the §24 "Additional terms" field is STAFF-only on the builder).
  // §25 notice block (always collected).
  noticeName?: string;
  noticeAddress?: string;
  noticeCityStateZip?: string;
  noticeEmail?: string;
}

/** The UI section a question belongs to (for on-screen grouping). */
export type OwnerIntakeSection =
  | 'taxes_insurance'
  | 'irrigation'
  | 'hoa'
  | 'notice';

/** The field kind the SPA renders for a question. */
export type OwnerIntakeQuestionKind = 'boolean' | 'select' | 'text' | 'textarea';

/** One server-computed question the owner may be asked. */
export interface OwnerIntakeQuestion {
  /** Stable id == the OwnerIntakeAnswers key it fills. */
  id: keyof OwnerIntakeAnswers;
  section: OwnerIntakeSection;
  kind: OwnerIntakeQuestionKind;
  prompt: string;
  /** For 'select' — the allowed values. */
  options?: Array<{ value: string; label: string }>;
  required: boolean;
  /**
   * Set for a CONFIRM-WITH-OWNER question: staff answered yes/no and asked to
   * confirm. The form presents "We think {yes/no} — just confirming," pre-selecting
   * `staffValue`; the owner confirms or corrects.
   */
  confirm?: { staffValue: boolean };
}

/**
 * The public form lifecycle state (server-driven; never recomputed client-side):
 *   • 'fillable'    — open for this owner to complete.
 *   • 'generating'  — a completion won the lock; the agreement is being written.
 *   • 'ready'       — the agreement is out for signature (signUrl set).
 *   • 'unavailable' — bad/dormant token, or the owner isn't on the record.
 */
export type OwnerIntakeState = 'fillable' | 'generating' | 'ready' | 'unavailable';

/** The intake VIEW returned by GET /api/owner-intake/:token (+ POST-progress). */
export interface OwnerIntakeFormView {
  state: OwnerIntakeState;
  /** The property address (form header). */
  propertyAddress: string;
  /** Joined owner party names — the "you and the other owners" framing. */
  ownerNames: string;
  /** The owner this link is for (display), or null on an unavailable view. */
  owner: { role: string; name: string } | null;
  /**
   * A default for the §25 notice email — THIS owner's email (else the first owner
   * with one). The form pre-populates the notice-email field with it when the owner
   * hasn't already entered one; the owner can still edit it.
   */
  noticeEmailDefault: string;
  /** The server-computed set of questions that APPLY (given prefill + answers). */
  questions: OwnerIntakeQuestion[];
  /** The owner's saved answers so far (echoed back so the form is resumable). */
  answers: OwnerIntakeAnswers;
  /**
   * Whether the property is in an HOA (server-resolved) → show the CC&R upload.
   * Authoritative signal (not the presence of the pay-dues question), so the CC&R
   * step still shows when staff finalized both the in-HOA and pay-dues answers.
   */
  ccrApplies: boolean;
  /** When state === 'ready': the owner's direct sign-page URL (absolute). */
  signUrl?: string;
}

/** POST /api/owner-intake/:token result. `view` is the refreshed view. */
export interface OwnerIntakeSubmitResult {
  ok: boolean;
  /**
   * Present only when THIS completion won the lock + the agreement auto-sent.
   * The page treats a truthy signUrl OR view.state==='ready' as "go sign".
   */
  state?: 'ready_to_sign';
  signUrl?: string;
  view?: OwnerIntakeFormView;
}

// ---------------------------------------------------------------------------
// Public (token-gated, no auth) client
// ---------------------------------------------------------------------------

/**
 * Error carrying the parsed body so the page can read `missing` (missing-required
 * 400) or `error` (injection 400 / other). Distinct class so a caught error is
 * recognizable from an unrelated throw.
 */
export class OwnerIntakeApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`Owner intake API error ${status}`);
    this.name = 'OwnerIntakeApiError';
  }

  /** The server's human message, if any. */
  get errorMessage(): string | null {
    return (this.body as { error?: string } | null)?.error ?? null;
  }

  /** The missing-required question ids on a 400 (empty otherwise). */
  get missing(): string[] {
    const m = (this.body as { missing?: unknown } | null)?.missing;
    return Array.isArray(m) ? (m as string[]) : [];
  }
}

async function publicRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // Only set JSON content-type for a JSON body — FormData must set its own
  // multipart boundary (never override it).
  if (init.body && typeof init.body === 'string' && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* empty */
    }
    throw new OwnerIntakeApiError(res.status, body);
  }
  return (await res.json()) as T;
}

/** GET the public, token-gated intake view. */
export function getOwnerIntake(token: string): Promise<OwnerIntakeFormView> {
  return publicRequest<OwnerIntakeFormView>(
    `/api/owner-intake/${encodeURIComponent(token)}`,
  );
}

/**
 * POST progress or completion. `{ answers }` saves partial progress; adding
 * `{ complete:true }` attempts the first-completion-wins lock + auto-send.
 * Returns the refreshed view (+ signUrl/state on a winning completion). A 400
 * (missing required / injection) throws OwnerIntakeApiError (read .missing).
 */
export function submitOwnerIntake(
  token: string,
  body: { answers?: OwnerIntakeAnswers; complete?: boolean },
): Promise<OwnerIntakeSubmitResult> {
  return publicRequest<OwnerIntakeSubmitResult>(
    `/api/owner-intake/${encodeURIComponent(token)}`,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** One known HOA offered in the WHICH-HOA dropdown (name + id only). */
export interface KnownHoaChoice {
  hoaId: string;
  name: string;
}

/** The sentinel `hoaChoice` value for the "New / not listed" option. */
export const HOA_CHOICE_NEW = '__new__';

/**
 * Fetch the READ-ONLY known-HOA list for the WHICH-HOA dropdown (token-gated). Returns
 * only names + ids for SELECTION — nothing here modifies an HOA. A bad/dormant token
 * or a not-in-HOA intake yields an empty list (never an error). Best-effort: any fetch
 * failure resolves to an empty list (the dropdown then shows only "New / not listed").
 */
export async function getHoaChoices(token: string): Promise<KnownHoaChoice[]> {
  try {
    const res = await publicRequest<{ hoas?: KnownHoaChoice[] }>(
      `/api/owner-intake/${encodeURIComponent(token)}/hoa-choices`,
    );
    return res.hoas ?? [];
  } catch {
    return [];
  }
}

// ---------------------------------------------------------------------------
// AUTHENTICATED staff — kick the intake off
// ---------------------------------------------------------------------------

export type OwnerIntakeOwnerType = 'individual' | 'entity';

/** One owner staff enter at kickoff. Email optional — a no-email owner can't be mailed. */
export interface OwnerIntakeOwnerInput {
  ownerType: OwnerIntakeOwnerType;
  firstName?: string;
  lastName?: string;
  entityName?: string;
  signerTitle?: string;
  email?: string;
  /**
   * The existing AppFolio owner staff linked this owner to (owner-sync spec §3).
   * Initiate only — sent in the request, NEVER rendered.
   */
  appfolioOwnerId?: string;
}

/** What staff set on the unified builder — per-field controls + the deal terms. */
export interface OwnerIntakeStaffPrefillInput {
  // §21 "items WHP manages" (taxes/insurance/mortgage) + irrigation/HOA — per-field controls.
  propertyTaxes: OwnerIntakeFieldControl;
  insurancePremiums: OwnerIntakeFieldControl;
  mortgage: OwnerIntakeFieldControl;
  hasIrrigation: OwnerIntakeFieldControl;
  needsWinterizing: OwnerIntakeFieldControl;
  winterizationInstructions?: string;
  inHoa: OwnerIntakeFieldControl;
  payHoaDues: OwnerIntakeFieldControl;
  managementPercent: number;
  placementPercent: number;
  targetRents?: string;
  minimumRents?: string;
  utilities?: Record<OwnerIntakeUtilityKey, OwnerIntakeUtilityChoice>;
  propertyType: OwnerIntakePropertyType;
  numberOfUnits?: number;
  startDate?: string;
}

/**
 * MODIFY re-send mode, set when the builder was re-opened from an out-for-signature
 * EPMA (`?from=<groupId>&mode=<...>`). Only the OWNER EMAIL COPY diverges server-side:
 *   • 'update'          — 0-signed simple update → the "your agreement has been
 *                         updated" notice instead of the first-time invite.
 *   • 'restart-signing' — partial-signed restart, carry existing answers → normal invite.
 *   • 'restart-intake'  — partial-signed re-collect the intake → normal invite.
 * Absent on a fresh first-time send and on the legacy intake-phase Modify.
 */
export type OwnerIntakeSendMode = 'update' | 'restart-signing' | 'restart-intake';

/** Body for POST /api/owner-intake/initiate. Property is address-only (NEW property). */
export interface InitiateOwnerIntakeBody {
  property: { address: string };
  owners: OwnerIntakeOwnerInput[];
  staffPrefill: OwnerIntakeStaffPrefillInput;
  /** MODIFY re-send mode — absent on a fresh send (see OwnerIntakeSendMode). */
  mode?: OwnerIntakeSendMode;
  /**
   * MODIFY minimal-intake supersede target — the groupId of the LIVE out-for-
   * signature agreement this new (minimal) intake replaces. The server silently
   * withdraws that old submission AFTER emailing the new intake. Absent on a fresh
   * send and on the intake-phase Modify.
   */
  supersedeGroupId?: string;
  /**
   * Owner-sync spec §3: the "Collect a new owner intake anyway" override. Sent only
   * when at least one owner is linked to an existing AppFolio owner; absent, the
   * server runs the chain exactly as today.
   */
  collectIntakeAnyway?: boolean;
}

/** Backend response for a successful initiate. */
export interface InitiateOwnerIntakeResult {
  groupId: string;
  status: string;
  ownersEmailed: number;
  owners: Array<{ role: string; name: string; hasEmail: boolean }>;
  /** Owner-sync spec §12: which chain steps run vs are skipped for existing owners. */
  chainPlan?: OwnerChainPlan;
}

/**
 * Thrown when the initiate route answers 503 — the feature is gated off
 * (EPMA_SEND_ENABLED / WHP_OWN_MESSAGING). The page catches this to render the
 * friendly "not enabled yet" state (mirrors epmaClient's EpmaNotEnabledError).
 */
export class OwnerIntakeNotEnabledError extends Error {
  constructor(message = 'Owner intake is not enabled yet.') {
    super(message);
    this.name = 'OwnerIntakeNotEnabledError';
  }
}

/** A permissive email shape check (matches the backend's permissiveness). */
export function isEmailValid(email: string): boolean {
  const e = email.trim();
  return e.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
}

// Regenerate-vs-intake decision (out-for-signature Modify) — the PURE logic lives
// in the leaf module ownerIntakeModify (no api/auth imports, unit-testable in
// isolation) and is re-exported here for app call sites.
export {
  ctlIsAsked,
  ctlResolves,
  epmaNewOwnerFields,
  type EpmaModifyControls,
} from './ownerIntakeModify';

/**
 * Kick off the owner intake: create the record + email each owner (with an email)
 * their per-owner intake link. Throws OwnerIntakeNotEnabledError on a 503 (feature
 * dormant); other errors propagate as ApiError.
 */
export async function initiateOwnerIntake(
  body: InitiateOwnerIntakeBody,
): Promise<InitiateOwnerIntakeResult> {
  try {
    const raw = await api.post<{
      groupId: string;
      status?: string;
      ownersEmailed?: number;
      owners?: Array<{ role: string; name: string; hasEmail: boolean }>;
      chainPlan?: OwnerChainPlan;
    }>('/api/owner-intake/initiate', body);
    return {
      groupId: raw.groupId,
      status: raw.status ?? 'intake_sent',
      ownersEmailed: raw.ownersEmailed ?? 0,
      owners: raw.owners ?? [],
      ...(raw.chainPlan ? { chainPlan: raw.chainPlan } : {}),
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new OwnerIntakeNotEnabledError(
        (err.body as { error?: string } | null)?.error ?? 'Owner intake is not enabled yet.',
      );
    }
    throw err;
  }
}

/**
 * Body for POST /api/owner-intake/:groupId/regenerate — the builder's current
 * (modified) config. Same shape as the initiate body MINUS `mode`; the route IS the
 * regenerate-and-send-for-signature path (no intake round-trip).
 */
export interface RegenerateOwnerAgreementBody {
  property: { address: string };
  owners: OwnerIntakeOwnerInput[];
  staffPrefill: OwnerIntakeStaffPrefillInput;
}

/** Backend response for a successful regenerate (agreement re-sent for signature). */
export interface RegenerateOwnerAgreementResult {
  groupId: string;
  status: string;
  ownersEmailed: number;
  owners: Array<{ role: string; name: string; hasEmail: boolean }>;
}

/**
 * REGENERATE + re-send an out-for-signature EPMA for signature (staff-only change,
 * no new owner input needed). Reuses the record's existing owner answers, builds a
 * fresh DocuSeal submission, and emails the owners the "your agreement has been
 * updated — please sign" notice (the SIGN link, not the intake form). Throws
 * OwnerIntakeNotEnabledError on a 503; a 409 (agreement fully executed, or the change
 * actually needs owner input) propagates as ApiError for the caller to surface.
 */
export async function regenerateOwnerAgreement(
  groupId: string,
  body: RegenerateOwnerAgreementBody,
): Promise<RegenerateOwnerAgreementResult> {
  try {
    const raw = await api.post<{
      groupId?: string;
      status?: string;
      ownersEmailed?: number;
      owners?: Array<{ role: string; name: string; hasEmail: boolean }>;
    }>(`/api/owner-intake/${encodeURIComponent(groupId)}/regenerate`, body);
    return {
      groupId: raw.groupId ?? groupId,
      status: raw.status ?? 'ready_to_sign',
      ownersEmailed: raw.ownersEmailed ?? 0,
      owners: raw.owners ?? [],
    };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new OwnerIntakeNotEnabledError(
        (err.body as { error?: string } | null)?.error ?? 'Owner intake is not enabled yet.',
      );
    }
    throw err;
  }
}

export interface ResendOwnerIntakeResult {
  groupId: string;
  ownersEmailed: number;
}

/**
 * Staff RESEND of a stalled owner intake: re-email the gentle follow-up reminder to
 * each still-incomplete owner and re-anchor the auto-reminder clock. Valid only while
 * the intake is still awaiting the owner (the backend 409s otherwise) and rate-limited
 * by a 12h server cooldown (429). Throws OwnerIntakeNotEnabledError on a 503;
 * other errors propagate as ApiError (the caller surfaces .body.error / .status).
 */
export async function resendOwnerIntake(groupId: string): Promise<ResendOwnerIntakeResult> {
  try {
    const raw = await api.post<{ groupId?: string; ownersEmailed?: number }>(
      `/api/owner-intake/${encodeURIComponent(groupId)}/resend`,
    );
    return { groupId: raw.groupId ?? groupId, ownersEmailed: raw.ownersEmailed ?? 0 };
  } catch (err) {
    if (err instanceof ApiError && err.status === 503) {
      throw new OwnerIntakeNotEnabledError(
        (err.body as { error?: string } | null)?.error ?? 'Owner intake is not enabled yet.',
      );
    }
    throw err;
  }
}

// ---------------------------------------------------------------------------
// AUTHENTICATED staff — MANAGE an intake-phase EPMA (cancel / swap email / detail)
// ---------------------------------------------------------------------------

/** The stored owner-intake lifecycle status (incl. the soft-cancel terminal). */
export type OwnerIntakeRecordStatus =
  | 'intake_sent'
  | 'in_progress'
  | 'generating'
  | 'ready_to_sign'
  | 'cancelled';

/** One owner as returned by the staff detail read (name + email + completed state). */
export interface OwnerIntakeDetailOwner {
  /** Per-owner token role (`Owner{N}`, 1-based) — the stable owner key. */
  role: string;
  name: string;
  email: string;
  ownerType: OwnerIntakeOwnerType;
  /** First-completion-wins → true only once the whole intake locked/sent. */
  completed: boolean;
}

/** GET /api/owner-intake/:groupId/detail — the staff-facing engagement config. */
export interface OwnerIntakeDetail {
  groupId: string;
  status: OwnerIntakeRecordStatus;
  round: number;
  property: { address: string };
  /** The full builder seed — per-field controls (taxes/insurance/mortgage/irrigation/
   *  HOA) AND the deal terms. */
  staffPrefill: OwnerIntakeStaffPrefillInput;
  owners: OwnerIntakeDetailOwner[];
  /** Whatever answers an owner has submitted so far. */
  answers: OwnerIntakeAnswers;
  /** Has ANY owner submitted answers yet? Drives redo-vs-use-existing on re-open. */
  intakeGathered: boolean;
  reminders: { count: number; lastReminderAt?: string; anchorAt?: string };
  createdAt: string;
  updatedAt: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelledByDisplay?: string;
  cancelledReason?: string;
  /**
   * Inside the City of Walla Walla limits? (docs/city-utility-agreement-plan.md §3).
   * null = never checked; absent from an older API.
   */
  jurisdiction?: OwnerIntakeJurisdiction | null;
}

/** POST /api/owner-intake/:groupId/cancel — the updated (cancelled) record summary. */
export interface CancelOwnerIntakeResult {
  ok: boolean;
  intake: {
    groupId: string;
    status: OwnerIntakeRecordStatus;
    cancelledAt?: string;
    cancelledBy?: string;
    cancelledByDisplay?: string;
    cancelledReason?: string;
  } | null;
}

/** PATCH /api/owner-intake/:groupId/owners/:ownerKey/email — swap result. */
export interface SwapOwnerIntakeEmailResult {
  ok: boolean;
  groupId: string;
  owner: { role: string; name: string; email: string };
  /** Whether the re-invite to the NEW address was dispatched. */
  reinvited: boolean;
}

/** Map a 503 to the friendly "not enabled" error; re-throw everything else. */
function rethrowOwnerIntakeError(err: unknown): never {
  if (err instanceof ApiError && err.status === 503) {
    throw new OwnerIntakeNotEnabledError(
      (err.body as { error?: string } | null)?.error ?? 'Owner intake is not enabled yet.',
    );
  }
  throw err;
}

/**
 * Load the staff-facing detail for an intake-phase EPMA — the full config for
 * re-opening the builder / managing the engagement. Throws OwnerIntakeNotEnabledError
 * on a 503; ApiError (404 etc.) otherwise.
 */
export async function getOwnerIntakeDetail(groupId: string): Promise<OwnerIntakeDetail> {
  try {
    return await api.get<OwnerIntakeDetail>(
      `/api/owner-intake/${encodeURIComponent(groupId)}/detail`,
    );
  } catch (err) {
    rethrowOwnerIntakeError(err);
  }
}

/**
 * Soft-cancel an intake-phase EPMA (an owner never completed the form). Keeps the
 * row (audit) but stops reminders + closes the owner form. Valid only while still
 * awaiting the owner — the backend 409s a locked/sent/already-cancelled intake.
 * Throws OwnerIntakeNotEnabledError on a 503; ApiError otherwise.
 */
export async function cancelOwnerIntake(
  groupId: string,
  reason?: string,
): Promise<CancelOwnerIntakeResult> {
  try {
    return await api.post<CancelOwnerIntakeResult>(
      `/api/owner-intake/${encodeURIComponent(groupId)}/cancel`,
      reason !== undefined ? { reason } : undefined,
    );
  } catch (err) {
    rethrowOwnerIntakeError(err);
  }
}

/**
 * Swap ONE owner's email on an intake-phase EPMA (PRE-DocuSeal). `ownerKey` is the
 * per-owner token role (`Owner{N}`, 1-based). Re-sends the intake invite to the NEW
 * address + re-anchors reminders. Valid only while the intake is still open — the
 * backend 409s once it locks/sends/cancels. Throws OwnerIntakeNotEnabledError on a
 * 503; ApiError (400 bad email / 404 owner not found) otherwise.
 */
export async function swapOwnerIntakeOwnerEmail(
  groupId: string,
  ownerKey: string,
  email: string,
): Promise<SwapOwnerIntakeEmailResult> {
  try {
    return await api.patch<SwapOwnerIntakeEmailResult>(
      `/api/owner-intake/${encodeURIComponent(groupId)}/owners/${encodeURIComponent(
        ownerKey,
      )}/email`,
      { email },
    );
  } catch (err) {
    rethrowOwnerIntakeError(err);
  }
}

/**
 * Staff set "inside the City of Walla Walla?" on the owner file (PATCH
 * /api/owner-intake/:groupId/jurisdiction). Returns the stored decision as the
 * detail read shows it. Throws OwnerIntakeNotEnabledError on a 503; ApiError (400
 * bad value / 404 no intake) otherwise.
 */
export async function setOwnerIntakeJurisdiction(
  groupId: string,
  jurisdiction: Jurisdiction,
): Promise<OwnerIntakeJurisdiction | null> {
  try {
    const raw = await api.patch<{ jurisdiction?: OwnerIntakeJurisdiction | null }>(
      `/api/owner-intake/${encodeURIComponent(groupId)}/jurisdiction`,
      { jurisdiction },
    );
    return raw.jurisdiction ?? null;
  } catch (err) {
    rethrowOwnerIntakeError(err);
  }
}
